/*
 * Yadid CRM enum labels — single source of truth for humanised
 * presentation of every enum defined in `backend/models.py`.
 *
 * Iter 86.7 · Extracted after QA flagged inline Select dropdowns on
 * /clients and /engagements showing raw enum values ('growth',
 * 'at_risk', 'on_hold', 'yellow', 't_and_m') while the polished
 * ClientEditDialog/ContactDirectory rendered 'Growth', 'At risk', etc.
 *
 * Rules:
 * 1. The `.value` field is the wire value the backend accepts (unchanged
 *    from `models.py` literals) — never rewrite it here.
 * 2. The `.label` field is the humanised display string used anywhere
 *    the user sees this enum: Select options, badges, filter chips.
 * 3. LABEL maps let non-Select surfaces (dots, badges, cards) look up
 *    a label without recomputing the tuple lookup at every render.
 */

// ─── Client ─────────────────────────────────────────────────────────────
export const CLIENT_TIERS = [
  { value: "strategic", label: "Strategic" },
  { value: "growth", label: "Growth" },
  { value: "opportunistic", label: "Opportunistic" },
];
export const CLIENT_TIER_LABEL = Object.fromEntries(
  CLIENT_TIERS.map((t) => [t.value, t.label]),
);

export const CLIENT_HEALTHS = [
  { value: "strong", label: "Strong" },
  { value: "stable", label: "Stable" },
  { value: "at_risk", label: "At risk" },
  { value: "critical", label: "Critical" },
];
export const CLIENT_HEALTH_LABEL = Object.fromEntries(
  CLIENT_HEALTHS.map((h) => [h.value, h.label]),
);

// ─── Engagement ─────────────────────────────────────────────────────────
export const ENGAGEMENT_STATUSES = [
  { value: "scoping", label: "Scoping" },
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On hold" },
  { value: "delivered", label: "Delivered" },
  { value: "closed", label: "Closed" },
];
export const ENGAGEMENT_STATUS_LABEL = Object.fromEntries(
  ENGAGEMENT_STATUSES.map((s) => [s.value, s.label]),
);

export const ENGAGEMENT_STAGES = [
  { value: "diagnostic", label: "Diagnostic" },
  { value: "design", label: "Design" },
  { value: "implementation", label: "Implementation" },
  { value: "review", label: "Review" },
];
export const ENGAGEMENT_STAGE_LABEL = Object.fromEntries(
  ENGAGEMENT_STAGES.map((s) => [s.value, s.label]),
);

// Health uses the traffic-light dots as the primary signal, but the
// dropdown option / screen-reader label still deserves a real word.
export const ENGAGEMENT_HEALTHS = [
  { value: "green", label: "Healthy" },
  { value: "yellow", label: "Watch" },
  { value: "red", label: "At risk" },
];
export const ENGAGEMENT_HEALTH_LABEL = Object.fromEntries(
  ENGAGEMENT_HEALTHS.map((h) => [h.value, h.label]),
);

export const BILLING_MODELS = [
  { value: "fixed", label: "Fixed fee" },
  { value: "t_and_m", label: "Time & materials" },
  { value: "retainer", label: "Retainer" },
  { value: "success_fee", label: "Success fee" },
];
export const BILLING_MODEL_LABEL = Object.fromEntries(
  BILLING_MODELS.map((b) => [b.value, b.label]),
);

// engagement_type has 9 canonical literals per models.py::Engagement.
// The four "canonical" YadidOS types (trusted_advisor, strategic_initiative,
// executive_convening, executive_communication) are the ones the new-
// engagement dialog exposes; the five legacy types (strategy, operations,
// transformation, diligence, advisory_retainer) exist on older records
// and still need humanised labels when rendered anywhere in the UI.
export const ENGAGEMENT_TYPES = [
  { value: "trusted_advisor", label: "Trusted Advisor" },
  { value: "strategic_initiative", label: "Strategic Initiative" },
  { value: "executive_convening", label: "Executive Convening" },
  { value: "executive_communication", label: "Executive Comms & Facilitation" },
  { value: "strategy", label: "Strategy" },
  { value: "operations", label: "Operations" },
  { value: "transformation", label: "Transformation" },
  { value: "diligence", label: "Diligence" },
  { value: "advisory_retainer", label: "Advisory Retainer" },
];
export const ENGAGEMENT_TYPE_LABEL = Object.fromEntries(
  ENGAGEMENT_TYPES.map((t) => [t.value, t.label]),
);

// ─── Task ───────────────────────────────────────────────────────────────
// Iter 91.9 · Extend the enum-label sweep to task priorities and note
// types that were still rendering as raw slugs on the Tasks / Advisory
// Desk pages. Priority order intentionally matches the visual weight of
// the priority chip (urgent → destructive tone, low → muted).
export const TASK_PRIORITIES = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
export const TASK_PRIORITY_LABEL = Object.fromEntries(
  TASK_PRIORITIES.map((p) => [p.value, p.label]),
);

export const TASK_STATUSES = [
  { value: "backlog", label: "Backlog" },
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "review", label: "Review" },
  { value: "done", label: "Done" },
];
export const TASK_STATUS_LABEL = Object.fromEntries(
  TASK_STATUSES.map((s) => [s.value, s.label]),
);

// ─── Note ───────────────────────────────────────────────────────────────
// Labels are anchored to the app-wide "Partner organization" terminology
// (client_briefing surfaces as "Partner briefing") for parity with the
// Advisory Desk hero tiles which use Partner / Engagement / Activity.
export const NOTE_TYPES = [
  { value: "meeting", label: "Meeting" },
  { value: "insight", label: "Insight" },
  { value: "strategic_memo", label: "Strategic memo" },
  { value: "call_log", label: "Call log" },
  { value: "diagnostic_memo", label: "Diagnostic memo" },
  { value: "weekly_brief", label: "Weekly brief" },
  { value: "health_storyline", label: "Health storyline" },
  { value: "client_briefing", label: "Partner briefing" },
  { value: "engagement_briefing", label: "Engagement briefing" },
  { value: "activity_briefing", label: "Activity briefing" },
  { value: "followup", label: "Follow-up" },
];
export const NOTE_TYPE_LABEL = Object.fromEntries(
  NOTE_TYPES.map((t) => [t.value, t.label]),
);
